import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import CveDetail from './CveDetail';
import { hasUsageData, programKey, unusedKeySet, usageIndex, usageOf } from './cveUsage';

// One machine's software: the known vulnerabilities (CVEs) of its programs first, then every installed program with
// when it last ran and how many CVEs it has. `data` is the whole result of the installed-programs script.
function SoftwareTab({ data }) {
  const programs = useMemo(() => (Array.isArray(data?.programs) ? data.programs : []), [data]);

  const rows = useMemo(() => {
    const cveCounts = new Map();
    (Array.isArray(data?.cve?.vulnerablePrograms) ? data.cve.vulnerablePrograms : []).forEach((v) => {
      cveCounts.set(programKey(v.name, v.version), Number(v.cveCount) || 0);
    });
    const showUsage = hasUsageData(data);
    const usage = usageIndex(data);
    const unusedKeys = unusedKeySet(data);
    return programs.map((p) => ({
      name: p.name,
      version: p.version,
      publisher: p.publisher,
      source: p.source,
      lastRun: p.lastRunDate || '',
      use: showUsage ? usageOf(p, usage, unusedKeys).label : '',
      cves: cveCounts.get(programKey(p.name, p.version)) || 0,
    }));
  }, [data, programs]);

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Program', size: 300 },
    { accessorKey: 'version', header: 'Version', size: 140 },
    { accessorKey: 'publisher', header: 'Publisher', size: 220 },
    { accessorKey: 'lastRun', header: 'Last run', size: 120 },
    { accessorKey: 'use', header: 'Use', size: 170 },
    { accessorKey: 'cves', header: 'CVEs', size: 90 },
    { accessorKey: 'source', header: 'Source', size: 100 },
  ], []);

  return (
    <Stack spacing={3}>
      <CveDetail data={data} />
      {programs.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
            Installed programs ({programs.length})
          </Typography>
          <MaterialReactTable
            columns={columns}
            data={rows}
            enableDensityToggle={false}
            enablePagination={false}
            initialState={{ sorting: [{ id: 'name', desc: false }], density: 'compact' }}
            muiTableContainerProps={{ sx: { maxHeight: 520 } }}
          />
        </Box>
      )}
    </Stack>
  );
}

export default SoftwareTab;
